import React, { createContext, useContext, useState, useEffect } from "react";
import axiosInstance from "../utils/axiosInstance";
import { message } from "antd";

const UserContext = createContext();

const getUserId = (user) => {
  return user?.id ?? user?._id ?? null;
};

const idsEqual = (a, b) => {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  return String(a) === String(b);
};

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const [filters, setFilters] = useState({
    search: "",
    role: "",
    isApproved: true,
  });

  const pageSize = 10;

  const currentUserRole = (
    localStorage.getItem("userRole") || "admin"
  ).toLowerCase();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: filters.search,
        role: filters.role,
        isApproved: filters.isApproved,
      };

      const response = await axiosInstance.get("/admin/users", { params });
      const raw = response.data.users || [];

      const normalized = raw
        .map((u) => ({ ...u, id: getUserId(u) ?? u.id ?? u._id }))
        .filter((user) => user.isApproved);

      normalized.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setUsers(normalized);
      setTotalUsers(response.data.total ?? normalized.length);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch users");
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updatedData) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}`, updatedData);
      const updatedUserFromServer = response.data?.user ?? null;

      const authoritative = updatedUserFromServer
        ? { ...updatedUserFromServer, id: getUserId(updatedUserFromServer) ?? updatedUserFromServer.id }
        : { ...updatedData, id: userId };

      setUsers((prevUsers) => {
        if (authoritative.isApproved === false) {
          return prevUsers.filter((u) => !idsEqual(getUserId(u), userId));
        }

        const exists = prevUsers.some((u) => idsEqual(getUserId(u), userId));
        if (exists) {
          return prevUsers.map((user) =>
            idsEqual(getUserId(user), userId) ? { ...user, ...authoritative } : user
          );
        }

        return [...prevUsers, authoritative];
      });

      message.success("User updated successfully");
      return authoritative;
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update user");
      throw err;
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      setUsers((prevUsers) =>
        prevUsers.filter((user) => !idsEqual(getUserId(user), userId))
      );
      message.success("User deleted successfully");
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete user");
      throw err;
    }
  };

  const updateCreditLimit = async (userId, creditLimit) => {
    try {
      const response = await axiosInstance.put(`/admin/user_credit/${userId}`, {
        credit_limit: creditLimit,
      });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          idsEqual(getUserId(user), userId)
            ? { ...user, credit_limit: creditLimit, ...(response.data?.user ?? {}) }
            : user
        )
      );

      message.success("Credit limit updated successfully");
      return response.data?.user ?? { id: userId, credit_limit: creditLimit };
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update credit limit");
      throw err;
    }
  };

  const approveUser = async (userId) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/approve`);
      const updatedUser = response.data?.user ?? null;

      if (!updatedUser) {
        message.success("User approved successfully");
        return null;
      }

      const authoritative = { ...updatedUser, id: getUserId(updatedUser) ?? updatedUser.id };

      setUsers((prevUsers) => {
        const exists = prevUsers.some((u) => idsEqual(getUserId(u), userId));
        if (exists) {
          return prevUsers.map((u) =>
            idsEqual(getUserId(u), userId) ? { ...u, ...authoritative, isApproved: true } : u
          );
        }
        return [...prevUsers, authoritative];
      });

      message.success("User approved successfully");
      return authoritative;
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to approve user");
      throw err;
    }
  };

  const rejectUser = async (userId, reason = "") => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/reject`, {
        reason: reason || null,
      });
      const updatedUser = response.data?.user ?? null;

      setUsers((prevUsers) =>
        prevUsers.filter((u) => !idsEqual(getUserId(u), userId))
      );

      message.success("User rejected successfully");
      return updatedUser;
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to reject user");
      throw err;
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  const value = {
    users,
    loading,
    error,
    currentPage,
    totalUsers,
    pageSize,
    filters,
    currentUserRole,
    setCurrentPage,
    setFilters,
    fetchUsers,
    updateUser,
    deleteUser,
    updateCreditLimit,
    approveUser,
    rejectUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUsers = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUsers must be used within a UserProvider");
  }
  return context;
};